import { heroVerifications } from '@/mocks/playgroundData';
import BioStampCard from '@/components/feature/BioStampCard';
import { useInView } from '@/hooks/useInView';

export default function PlaygroundHero() {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <section className="relative w-full overflow-hidden bg-background-50 pt-28 md:pt-32 lg:pt-36 pb-14 md:pb-20">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20abstract%20biometric%20technology%20background%20with%20a%20delicate%20glowing%20fingerprint%20motif%2C%20pale%20aqua%20teal%20and%20soft%20cyan%20gradient%20mesh%20on%20a%20light%20airy%20canvas%2C%20minimal%20elegant%20corporate%20design%2C%20generous%20negative%20space%2C%20subtle%20geometric%20grid%20lines%20and%20gentle%20luminous%20nodes&width=1600&height=900&seq=playground-hero-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/90 to-background-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50/70 via-background-50/40 to-transparent" />
      </div>

      <div ref={ref} className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column */}
          <div
            className={`transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-primary-100 border border-primary-200 rounded-full mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
              <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">
                bioVerifier Playground
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-[1.08] mb-5"
              style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
            >
              Identity you can verify.
              <br />
              Messages you can trust.
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl mb-8">
              Discover how bioMail and bioVerifier use biometric identity and the bioStamp to secure
              your email, invoices and personal information.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="#send-stamp"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-user-line text-base" />
                For Individuals
              </a>
              <a
                href="#explore"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-foreground-900 border border-background-300 font-medium rounded-full hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-building-2-line text-base" />
                For Business
              </a>
            </div>
          </div>

          {/* Right Column */}
          <div
            className={`transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '120ms' }}
          >
            <div className="rounded-2xl bg-background-50/70 backdrop-blur-sm border border-background-200/70 p-6 md:p-8">
              <BioStampCard className="mb-6" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {heroVerifications.map((item) => (
                  <div key={item.id} className="flex items-start gap-3">
                    <div className="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-lg`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-0.5">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}